import { useState, useEffect, useRef } from 'react';
import { useDebug } from '../context/DebugContext';
import { useTransportMode } from '../hooks/useHttpPolling';
import './Messages.css';

interface LatticeMessage {
  id: string;
  path: string;
  payload: Record<string, unknown>;
  timestamp: number;
  source: string;
}

export default function Messages() {
  const { addEvent } = useDebug();
  const { mode } = useTransportMode();
  const [messages, setMessages] = useState<LatticeMessage[]>([]);
  const [filter, setFilter] = useState('');
  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    addEvent({
      source: 'console',
      category: 'navigation',
      message: 'Viewed Messages page',
    });
  }, [addEvent]);

  // Real-time message subscription (requires WebTransport)
  // TODO: When #191 deploys, subscribe to lattice messages here
  // For now, messages will only appear when WebTransport is connected

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, autoScroll]);

  const filteredMessages = messages.filter(
    (msg) =>
      !filter ||
      msg.path.toLowerCase().includes(filter.toLowerCase()) ||
      msg.source.includes(filter)
  );

  const formatTime = (ts: number) => {
    const date = new Date(ts);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }) + '.' + String(date.getMilliseconds()).padStart(3, '0');
  };

  const getPathColor = (path: string) => {
    if (path.includes('payment')) return '#44ff88';
    if (path.includes('user')) return '#4488ff';
    if (path.includes('circuit')) return '#ff8844';
    if (path.includes('metrics')) return '#888';
    if (path.includes('consensus')) return '#ff44ff';
    if (path.includes('bridge')) return '#ffff44';
    return '#666';
  };

  return (
    <div className="messages-page">
      <div className="page-header">
        <h2>Live Message Stream</h2>
        <div className="header-controls">
          <input
            type="text"
            placeholder="Filter by path or source..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-input"
          />
          <button
            className={`control-btn ${autoScroll ? 'active' : ''}`}
            onClick={() => setAutoScroll(!autoScroll)}
          >
            {autoScroll ? '📌 Auto-scroll' : '📌 Manual'}
          </button>
          <button
            className={`control-btn ${paused ? 'paused' : ''}`}
            onClick={() => setPaused(!paused)}
          >
            {paused ? '▶ Resume' : '⏸ Pause'}
          </button>
          <button
            className="control-btn"
            onClick={() => setMessages([])}
          >
            🗑 Clear
          </button>
        </div>
      </div>

      <div className="messages-stats">
        <span className="stat">
          <span className="stat-value">{messages.length}</span>
          <span className="stat-label">Total</span>
        </span>
        <span className="stat">
          <span className="stat-value">{filteredMessages.length}</span>
          <span className="stat-label">Showing</span>
        </span>
        <span className="stat">
          <span className="stat-value" style={{ color: paused ? '#ff4444' : '#44ff88' }}>
            {paused ? 'PAUSED' : 'LIVE'}
          </span>
          <span className="stat-label">Status</span>
        </span>
      </div>

      <div className="messages-container">
        <div className="messages-list">
          {filteredMessages.length === 0 ? (
            <div className="empty">
              {filter ? 'No messages match filter' : (
                mode === 'http-polling' ? (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ marginBottom: '8px' }}>Real-time messages require WebTransport</p>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                      Currently in HTTP polling mode. See issue #191.
                    </p>
                  </div>
                ) : 'Waiting for messages...'
              )}
            </div>
          ) : (
            filteredMessages.map((msg) => (
              <div key={msg.id} className="message-row">
                <span className="msg-time">{formatTime(msg.timestamp)}</span>
                <span className="msg-source">{msg.source}</span>
                <span
                  className="msg-path"
                  style={{ color: getPathColor(msg.path) }}
                >
                  {msg.path}
                </span>
                <span className="msg-payload">
                  {JSON.stringify(msg.payload)}
                </span>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
}
